import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';

import { MaterialTable, ActionBar, Fallback, Footer, Button, ConfirmModal as ConfirmPopUp } from '../../components';
import { viewTypes } from '../../constants';

import { IListingComponentProps, IListingState } from './types'
import logo from '../../assets/images';

import './listing.scss';
import { useDataFetcher, apiHelper } from '../../hooks';
import { useDispatch } from 'react-redux';
import { usePages } from '../../hooks';
import { useAppSelector } from '../../redux';
import { enqueueSnackbar } from 'notistack';

const ListingComponent: React.FC<IListingComponentProps> = ({
    title,
    tableColumns,
    columnOrder = ['mrt-row-select'],
    rowActionMenu,
    destructiveActionMenu,
    handleAddButton,
    searchText,
    handleSearch,
    disabledViews = ['Kanban', 'Calendar', 'Gantt'],
    fields,
    selectedIds = [],
    pageName,
    importModuleName,
    showAddButton = true,
    actionButtons,
    paginationResource,
    enablePages = false,
    GridComponent,
    onSelectRow,
    onColumnOrdering,
    onColumnVisibility,
    onSortingChange,
    fallbackIcon,
    className = '',
    onDeleteConfirm,
    deleteTitle,
    deleteDescription,
    fetchApi,
    resetState,
    setPaginationModel,
    deleteApi,
    storeName,
    isUseFields,
    customeFilter,
    customFields,
    isLoading: loadinState,
    customRows = [],
    ...rest
}) => {
    const { t } = useTranslation();

    //dispatch
    const dispatch = useDispatch()

    //Page Context
    const { activePage, updatePageInfo } = usePages();
    //Store 
    const {
        paginationModel,
        rows,
        isLoading,
    } = useAppSelector((store) => store?.[storeName]);


    const { pageNo, limit: pLimit, totalCount } = paginationModel;
    const limit = activePage.page_size || pLimit;
    const skip = limit * (pageNo - 1);


    // Local state
    const [listingState, setListingState] = useState<IListingState>({
        selectedRows: selectedIds,
        itemToDelete: null,
        isDeleting: false
    });

    // Calculate pagination values
    const total = Math.ceil(totalCount / limit);

    // Memoized table data
    const tableRows = useMemo(() => customRows?.length ? customRows : rows, [rows, customRows]);

    // Handle row selection
    const handleRowSelection = (selectedRows: any[]) => {
        const selectedRowIds = selectedRows.map((row) => row.original.id);
        setListingState(prev => ({ ...prev, selectedRows: selectedRowIds }));
        onSelectRow?.(selectedRows);
    };

    // Handle pagination changes
    const handlePaginationChange = (model: any) => {
        if (activePage.page_size !== model?.limit) {
            updatePageInfo({ page_size: model.limit });
        }
        handlePaginationModel(model);
    };

    // Handle search
    const handleSearchChange = (search: string) => {
        updatePageInfo({ search });
        handlePaginationModel({ pageNo: 1 });
        handleSearch?.(search);
    };

    // Handle delete confirmation
    const handleDeleteClick = (item: any) => {
        setListingState(prev => ({ ...prev, itemToDelete: item }));
    };

    const handleDeleteConfirm = async () => {
        if (!listingState.itemToDelete) return;

        setListingState(prev => ({ ...prev, isDeleting: true }));

        try {
            onDeleteConfirm ? await onDeleteConfirm(listingState.itemToDelete) : await deleteRecord(listingState.itemToDelete);
            setListingState(prev => ({ ...prev, itemToDelete: null, isDeleting: false }));
        } catch (error) {
            setListingState(prev => ({ ...prev, isDeleting: false }));
        }
    };

    const handleDeleteCancel = () => {
        setListingState(prev => ({ ...prev, itemToDelete: null }));
    };

    // Enhance destructive actions to include delete confirmation
    const enhancedDestructiveActions = useMemo(() => {
        if (!destructiveActionMenu) return undefined;

        return destructiveActionMenu.map(action => ({
            ...action,
            handleAction: action.label.toLowerCase().includes('delete')
                ? handleDeleteClick
                : action.handleAction
        }));
    }, [destructiveActionMenu]);

    //Handle pagination model
    const handlePaginationModel = (model: IPaginationModel) => {
        if (activePage.page_size != model?.limit) {
            updatePageInfo({ page_size: model.limit });
        }
        dispatch(setPaginationModel(model));
    }

    //Delete
    const deleteRecord = async (requestToDelete: any) => {
        const response: any = await dispatch(deleteApi(String(requestToDelete.id)));
        apiHelper(response, () => {
            enqueueSnackbar(
                ` ${title}: ${requestToDelete?.id} ${t('common.msg.deleted')}`,
            )
            getData()
        })
    };




    //clean up
    const cleanUp = useCallback(() => {
        dispatch(resetState())
    }, [dispatch, resetState]);

    // Data fetching
    const getData = useDataFetcher(
        tableColumns?.length ? tableColumns : activePage,
        { skip, limit },
        fetchApi,
        customeFilter,
        [],
        true,
        customFields

    )

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        return cleanUp;
    }, [cleanUp]);

    useEffect(() => {
        if (activePage?.views?.length) return;
        updatePageInfo({
            active_view: viewTypes.TABLE,
            views: [
                {
                    label: 'Table',
                    position: 0
                }
            ]
        })
    }, [activePage?.views])

    return (
        <section className={`listing-component ${className}`}>
            <ActionBar
                title={title}
                data={activePage?.views || [
                    {
                        label: 'Table',
                        position: 0
                    }
                ]}
                setData={(views) => updatePageInfo({ views })}
                active={activePage.active_view}
                setActive={(tab) => updatePageInfo({ active_view: tab })}
                handleAddButton={handleAddButton}
                searchText={searchText || activePage.search || ''}
                handleSearch={handleSearchChange}
                disabledViews={disabledViews}
                fields={fields}
                isUseFields={isUseFields}
                selectedIds={listingState.selectedRows}
                pageName={pageName}
                importModuleName={importModuleName}
                button={showAddButton}
                actionBtn={actionButtons}
                {...rest}
            />


            <>
                {activePage?.active_view === viewTypes.TABLE && (
                    <MaterialTable
                        rows={tableRows || []}
                        columns={tableColumns}
                        paginationModel={paginationModel}
                        totalPages={total}
                        columnOrder={[...columnOrder, ...activePage.column_order]}
                        states={{ isLoading: loadinState || isLoading }}
                        onSortingChange={onSortingChange ? (sort) => {
                            updatePageInfo({ sort });
                            onSortingChange(sort);
                        } : undefined}
                        enableColumnDragging={false}
                        enableEditing={false}
                        rowActionMenu={rowActionMenu}
                        destructiveActionMenu={enhancedDestructiveActions}
                        isResetRow={false}
                        onSelectRow={handleRowSelection}
                        handleColumnOrdering={onColumnOrdering ? (colOrder) => {
                            const newOrder = colOrder.slice(1);
                            updatePageInfo({ column_order: newOrder });
                            onColumnOrdering(newOrder);
                        } : undefined}
                        onColumnVisibility={onColumnVisibility ? (columns) => {
                            updatePageInfo({ visible_columns: columns });
                            onColumnVisibility(columns);
                        } : undefined}
                    />
                )}

                {activePage?.active_view === viewTypes.GRID && GridComponent && (
                    <GridComponent
                        data={tableRows}
                        rowActionMenu={rowActionMenu}
                        destructiveActionMenu={enhancedDestructiveActions}
                    />
                )}

                {!activePage?.active_view && !isLoading && (
                    <Fallback
                        heading={title}
                        icon={fallbackIcon || <img src={logo.shippmentFallback} alt="fallback" />}
                    >
                        {showAddButton && handleAddButton && (
                            <Button
                                variant='contained'
                                startIcon={<AddIcon />}
                                onClick={handleAddButton}
                            >
                                {`${t('common.add')} ${t('common.new')}`}
                            </Button>
                        )}
                    </Fallback>
                )}

                <Footer
                    total={total}
                    paginationModel={{
                        ...paginationModel,
                        limit: activePage.page_size
                    }}
                    handlePaginationModel={handlePaginationChange}
                    resource={paginationResource}
                    enablePages={enablePages}
                />

                <ConfirmPopUp
                    open={Boolean(listingState.itemToDelete)}
                    onClose={handleDeleteCancel}
                    onConfirm={handleDeleteConfirm}
                    loading={listingState.isDeleting}
                    title={deleteTitle || `${t('common.delete')} ${title}`}
                    description={
                        deleteDescription
                            ? deleteDescription(listingState.itemToDelete)
                            : `${t('common.deleteMsg')} ${title}: ${listingState.itemToDelete?.id || listingState.itemToDelete?.name || ''} ?`
                    }
                />
            </>
        </section>
    );
};

export default ListingComponent;
